import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FileText, MessageSquare, Lightbulb, BookOpen, X, Search, CheckCircle } from 'lucide-react';
import MobileBottomSheet from './MobileBottomSheet';
import DocumentCard from '../ui/DocumentCard';
import { useChat } from '../../contexts/ChatContext';

import { STORAGE_KEYS } from '../../constants/storage';
import { getTheme } from '../../utils/theme';
import { loadGooglePicker, downloadDriveFile } from '../../utils/googleDrivePicker';
import { fileToBase64 } from '../../utils/fileUpload';
import { extractFileContent } from '../../utils/textExtractor';
import { downloadFileViaProxy } from '../../services/gemini';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../../contexts/AuthContext';
import { useDocuments } from '../../contexts/DocumentContext';
const ChatView = ({ rightSidebarOpen, onCloseSidebar }) => {
  const { messages, isLoading, error, streamingMessage, sendMessage: sendChatMessage, isGeminiAvailable } = useChat();
  const { currentUser } = useAuth();
  const { uploadDocument } = useDocuments();
  const fileInputRef = useRef(null);
  const [inputValue, setInputValue] = useState('');
  const [showAttachDropdown, setShowAttachDropdown] = useState(false);
  const [showCloudSubmenu, setShowCloudSubmenu] = useState(false);
  const [showMobileDocuments, setShowMobileDocuments] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const chatHistoryRef = useRef(null);
  const attachDropdownRefMobile = useRef(null);
  const attachButtonRefMobile = useRef(null);
  const attachDropdownRefDesktop = useRef(null);
  const attachButtonRefDesktop = useRef(null);
  const inputRef = useRef(null);
  const pendingMessageProcessed = useRef(false);

  // Derive isCompact from messages length - only compact if there are messages
  const isCompact = messages.length > 0;

  // Detect mobile (768px breakpoint)
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Open bottom sheet when triggered from TopNavigationMobile
  useEffect(() => {
    if (rightSidebarOpen && isMobile) {
      setShowMobileDocuments(true);
    } else if (!rightSidebarOpen) {
      setShowMobileDocuments(false);
    }
  }, [rightSidebarOpen, isMobile]);

  // Auto-scroll to bottom when new messages arrive or streaming message updates
  useEffect(() => {
    if (chatHistoryRef.current && (messages.length > 0 || streamingMessage)) {
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        if (chatHistoryRef.current) {
          chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
        }
      });
    }
  }, [messages, streamingMessage]);

  // Check for pending message from dashboard on mount
  useEffect(() => {
    if (pendingMessageProcessed.current) return;

    const pendingMessageStr = localStorage.getItem(STORAGE_KEYS.DASHBOARD_PENDING_MESSAGE);
    if (pendingMessageStr) {
      pendingMessageProcessed.current = true;

      try {
        const pendingMessage = JSON.parse(pendingMessageStr);

        // Clear the pending message
        localStorage.removeItem(STORAGE_KEYS.DASHBOARD_PENDING_MESSAGE);

        // Send the message using ChatContext
        sendChatMessage(pendingMessage.content);
      } catch (error) {
        console.error('Error parsing pending message:', error);
        localStorage.removeItem(STORAGE_KEYS.DASHBOARD_PENDING_MESSAGE);
        pendingMessageProcessed.current = false; // Allow retry on error
      }
    }
  }, [sendChatMessage]); // Only run on mount

  // Position dropdown dynamically - Mobile uses Portal with fixed positioning, Desktop uses CSS
  useEffect(() => {
    if (showAttachDropdown) {
      if (isMobile && attachButtonRefMobile.current && attachDropdownRefMobile.current) {
        // Mobile: Portal-based dropdown - position it above the button
        const button = attachButtonRefMobile.current;
        const dropdown = attachDropdownRefMobile.current;

        if (!button || !dropdown) {
          return;
        }

        // Start with dropdown hidden and positioned off-screen
        dropdown.style.setProperty('position', 'fixed', 'important');
        dropdown.style.setProperty('top', '-9999px', 'important');
        dropdown.style.setProperty('left', '-9999px', 'important');
        dropdown.style.setProperty('opacity', '0', 'important');
        dropdown.style.setProperty('visibility', 'hidden', 'important');
        dropdown.style.setProperty('pointer-events', 'none', 'important');
        dropdown.style.setProperty('display', 'block', 'important');
        dropdown.style.setProperty('z-index', '99999', 'important');
        dropdown.style.setProperty('transition', 'opacity 0.15s ease-out', 'important');

        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          // Get button position
          const buttonRect = button.getBoundingClientRect();

          // Force reflow to get dropdown dimensions
          void dropdown.offsetHeight;

          // Get dropdown dimensions
          const dropdownHeight = dropdown.offsetHeight || 200;
          const dropdownWidth = dropdown.offsetWidth || 200;

          // Calculate position above button
          const gap = 8;
          const buttonTop = buttonRect.top;
          const buttonLeft = buttonRect.left;

          // Position dropdown above button
          let top = buttonTop - dropdownHeight - gap;
          const minTop = 8;
          const finalTop = Math.max(minTop, top);

          // Handle horizontal positioning
          const screenMargin = 12;
          let finalLeft = buttonLeft;

          if (finalLeft + dropdownWidth > window.innerWidth - screenMargin) {
            finalLeft = window.innerWidth - dropdownWidth - screenMargin;
          }

          if (finalLeft < screenMargin) {
            finalLeft = screenMargin;
          }

          // Apply positioning
          dropdown.style.setProperty('top', `${finalTop}px`, 'important');
          dropdown.style.setProperty('left', `${finalLeft}px`, 'important');
          dropdown.style.setProperty('bottom', 'auto', 'important');
          dropdown.style.setProperty('right', 'auto', 'important');
          dropdown.style.setProperty('transform', 'none', 'important');

          // Show dropdown with smooth fade-in
          requestAnimationFrame(() => {
            dropdown.style.setProperty('visibility', 'visible', 'important');
            dropdown.style.setProperty('pointer-events', 'all', 'important');
            // Trigger opacity change for transition
            requestAnimationFrame(() => {
              dropdown.style.setProperty('opacity', '1', 'important');
            });
          });
        });

        // Cleanup function
        return () => {
          if (dropdown) {
            dropdown.style.setProperty('opacity', '0', 'important');
            dropdown.style.setProperty('visibility', 'hidden', 'important');
            dropdown.style.setProperty('pointer-events', 'none', 'important');
          }
        };
      } else {
        // Desktop: CSS handles everything via .show class - don't set inline styles
        // The .show class in CSS will handle visibility
      }
    } else {
      // Cleanup when closing - only for mobile
      if (isMobile && attachDropdownRefMobile.current) {
        const dropdown = attachDropdownRefMobile.current;
        dropdown.style.setProperty('opacity', '0', 'important');
        dropdown.style.setProperty('visibility', 'hidden', 'important');
        dropdown.style.setProperty('pointer-events', 'none', 'important');
      }
      // Desktop: Remove any inline styles that might have been set
      if (!isMobile && attachDropdownRefDesktop.current) {
        const dropdown = attachDropdownRefDesktop.current;
        // Remove inline styles so CSS can take over
        dropdown.style.removeProperty('opacity');
        dropdown.style.removeProperty('visibility');
        dropdown.style.removeProperty('pointer-events');
      }
    }
  }, [showAttachDropdown, isMobile]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!showAttachDropdown) return;

    const handleClickOutside = (event) => {
      // Only handle desktop clicks when on desktop
      if (!isMobile) {
        const desktopDropdown = attachDropdownRefDesktop.current;
        const desktopButton = attachButtonRefDesktop.current;

        // Check if dropdown is actually visible (has .show class)
        const isDropdownVisible = desktopDropdown && desktopDropdown.classList.contains('show');

        if (isDropdownVisible) {
          // Check if click is inside desktop dropdown or button
          const isClickInDropdown = desktopDropdown && desktopDropdown.contains(event.target);
          const isClickInButton = desktopButton && desktopButton.contains(event.target);
          const isClickInsideDesktop = isClickInDropdown || isClickInButton;

          // If click is outside desktop dropdown/button, close
          if (!isClickInsideDesktop) {
            setShowAttachDropdown(false);
            setShowCloudSubmenu(false);
          }
        }
      } else {
        // Mobile handling
        const isClickInsideMobile =
          (attachDropdownRefMobile.current && attachDropdownRefMobile.current.contains(event.target)) ||
          (attachButtonRefMobile.current && attachButtonRefMobile.current.contains(event.target));

        if (!isClickInsideMobile) {
          setShowAttachDropdown(false);
          setShowCloudSubmenu(false);
        }
      }
    };

    // Use a small delay to avoid closing immediately when opening
    const timeoutId = setTimeout(() => {
      // Use click event for better compatibility
      document.addEventListener('click', handleClickOutside, true);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [showAttachDropdown, isMobile]);

  const handleGoogleDrivePick = async () => {
    try {
      await loadGooglePicker(async (docs, accessToken) => {
        console.log("📁 Selected Drive files:", docs);

        const attachments = [];
        for (const doc of docs) {
          try {
            console.log(`Downloading file: ${doc.name} (${doc.id})...`);
            const fileData = await downloadDriveFile(doc.id, accessToken);

            // Extract text for DOCX/TXT
            let extractedText = null;
            if (fileData.mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
              fileData.mimeType === 'text/plain') {
              try {
                // Convert Base64 result back to Blob for extraction
                const binaryString = window.atob(fileData.data);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                  bytes[i] = binaryString.charCodeAt(i);
                }
                const blob = new Blob([bytes], { type: fileData.mimeType });
                extractedText = await extractFileContent(blob, doc.name);
                if (extractedText && typeof extractedText === 'string') {
                  extractedText = extractedText.replace(/<[^>]*>?/gm, '\n');
                }
              } catch (e) { console.error("Drive text extraction error:", e); }
            }

            attachments.push({
              mimeType: fileData.mimeType || doc.mimeType,
              data: fileData.data,
              url: doc.url || null,
              textContent: extractedText,
              name: doc.name,
              id: doc.id
            });
            console.log(`File downloaded: ${doc.name}`);
          } catch (err) {
            console.error(`Error downloading file ${doc.name}:`, err);
          }
        }

        if (attachments.length > 0) {
          // Queue attachments instead of sending immediately (Consistent with TaxperView)
          setPendingAttachments(prev => [...prev, ...attachments]);

          // Focus input
          setTimeout(() => {
            const input = document.getElementById(isMobile ? 'chatInput' : 'chatInputDesktop');
            if (input) input.focus();
          }, 100);
        } else {
          console.warn("No files were successfully downloaded.");
        }
      });
    } catch (error) {
      console.error('Error loading Google Drive Picker:', error);
    }

    setShowAttachDropdown(false);
    setShowCloudSubmenu(false);
  };

  const handleSend = async () => {
    if (!inputValue.trim() && pendingAttachments.length === 0) return;
    const messageContent = inputValue.trim();
    setInputValue('');
    const attachments = [...pendingAttachments];
    setPendingAttachments([]);

    // Process attachments:
    // 1. Identify valid attachments for Gemini (Images, PDFs)
    // 2. Identify DOCX/Text attachments to append as text content
    const geminiAttachments = [];
    let appendedContent = '';

    attachments.forEach(att => {
      if (att.textContent) {
        // It's a DOCX/Text file we extracted
        appendedContent += `\n\n--- Document: ${att.name} ---\n${att.textContent}\n-----------------\n`;
      } else if (
        att.mimeType?.toLowerCase().includes('word') ||
        att.mimeType?.toLowerCase().includes('officedocument') ||
        att.mimeType?.toLowerCase().includes('docx')
      ) {
        // Backup check: It's DOCX but no text content? Skip it for Gemini to avoid 400 error.
        console.warn('Skipping DOCX attachment for inlineData (not supported by Gemini):', att.name);
      } else {
        // Support PDF, Images, etc.
        geminiAttachments.push({
          mimeType: att.mimeType,
          url: att.url,
          data: att.data
        });
      }
    });

    // Send message via ChatContext with attachments
    await sendChatMessage(
      (messageContent + appendedContent) || (geminiAttachments.length > 0 ? `Please analyze: ${geminiAttachments.map(a => a.name).join(', ')}` : ''),
      null,
      geminiAttachments
    );
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleContainerClick = (e) => {
    // Focus the input when clicking anywhere in the container, except on buttons
    // Also exclude attach button and its wrapper
    if (e.target.closest('button') ||
      e.target.closest('.attach-btn-wrapper') ||
      e.target.closest('.attach-btn')) {
      return; // Don't focus if clicking a button or attach area
    }
    inputRef.current?.focus();
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !currentUser) return;

    try {
      // Process file LOCALLY (base64) without uploading to Storage
      // specific user request: "storage a bir şey yazmamlıyız"
      const base64Data = await fileToBase64(file);

      // Extract text content for DOCX/TXT to avoid Gemini's unsupported MIME type error
      let extractedText = null;
      if (
        file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        file.type === 'text/plain'
      ) {
        try {
          extractedText = await extractFileContent(file);
          // Strip HTML tags if it's from DOCX (mammoth returns HTML)
          if (extractedText && typeof extractedText === 'string') {
            extractedText = extractedText.replace(/<[^>]*>?/gm, '\n');
          }
        } catch (extractErr) {
          console.error('Error extracting text for chat:', extractErr);
        }
      }

      setPendingAttachments(prev => [...prev, {
        mimeType: file.type,
        url: null, // No URL since we didn't upload
        data: base64Data, // Include base64 data for Gemini (for PDF/Images)
        textContent: extractedText, // Include extracted text for DOCX
        name: file.name,
        id: Date.now()
      }]);

      // Focus input
      setTimeout(() => {
        const input = document.getElementById(isMobile ? 'chatInput' : 'chatInputDesktop');
        if (input) input.focus();
      }, 100);

    } catch (err) {
      console.error('Chat file upload failed:', err);
    } finally {
      // Critical: Reset file input so checking the same file again triggers onChange
      e.target.value = '';
      setShowAttachDropdown(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setInputValue(suggestion);
    setTimeout(() => handleSend(), 100);
  };

  // Try Asking suggestions for Chat
  const suggestions = [
    {
      id: 1,
      text: 'Explain tax law basics',
      category: 'TAX LAW',
      icon: BookOpen,
    },
    {
      id: 2,
      text: 'Review a legal contract',
      category: 'CONTRACT ANALYSIS',
      icon: FileText,
    },
    {
      id: 3,
      text: 'Financial planning advice',
      category: 'FINANCE',
      icon: Lightbulb,
    },
    {
      id: 4,
      text: 'General legal questions',
      category: 'LEGAL HELP',
      icon: MessageSquare,
    },
  ];

  const handleDocumentClick = (document) => {
    if (isMobile) {
      setShowMobileDocuments(false);
    }
  };

  // Default documents for Chat
  const documents = [
    {
      id: 1,
      title: 'Recent Conversation Summary',
      filename: 'chat-summary-2024.pdf',
      badge: 'Summary',
      badgeColor: '#3b82f6',
      loading: false,
      view: 'chat',
    },
    {
      id: 2,
      title: 'Legal Research Notes',
      filename: 'legal-notes.docx',
      badge: 'Notes',
      badgeColor: '#10b981',
      loading: false,
      view: 'chat',
    },
    {
      id: 3,
      title: 'Tax Advice Transcript',
      filename: 'tax-advice.pdf',
      badge: 'Transcript',
      badgeColor: '#8b5cf6',
      loading: false,
      view: 'chat',
    },
  ];

  // Filter documents by active filter
  const filteredDocs = activeFilter === 'all'
    ? documents
    : documents.filter(doc => doc.badge.toLowerCase() === activeFilter);

  // Filter by search query
  const searchFilteredDocs = searchQuery.trim()
    ? filteredDocs.filter(doc =>
      doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.filename.toLowerCase().includes(searchQuery.toLowerCase())
    )
    : filteredDocs;

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      // Handle external files (from computer)
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        console.log('📎 Files dropped in Chat:', files);
        // ChatView uses isLoading from context but doesn't have a local thinking state for upload
        // We can just upload and add to pending

        try {
          // Use local processing (Chat Only) instead of saving to Firestore/Storage
          const uploadPromises = files.map(async (file) => {
            const base64Data = await fileToBase64(file);

            let extractedText = null;
            if (
              file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
              file.type === 'text/plain'
            ) {
              try {
                extractedText = await extractFileContent(file);
                if (extractedText && typeof extractedText === 'string') {
                  extractedText = extractedText.replace(/<[^>]*>?/gm, '\n');
                }
              } catch (e) { console.error(e); }
            }

            return {
              mimeType: file.type,
              url: null, // No URL
              data: base64Data,
              textContent: extractedText,
              name: file.name,
              id: Date.now() + Math.random()
            };
          });

          const uploadedFiles = await Promise.all(uploadPromises);
          setPendingAttachments(prev => [...prev, ...uploadedFiles]);

          setTimeout(() => {
            const input = document.getElementById(isMobile ? 'chatInput' : 'chatInputDesktop');
            if (input) input.focus();
          }, 100);
        } catch (err) {
          console.error('External file drop upload failed:', err);
        }
        return;
      }

      // Handle internal application items
      const data = e.dataTransfer.getData('application/json');
      if (!data) return;

      const doc = JSON.parse(data);
      console.log('📎 Document dropped in Chat (Queued):', doc);

      if (doc.url && doc.name) {
        let base64Data = null;
        let extractedText = null;

        // Skip direct fetch for Firebase Storage to avoid CORS
        const isFirebaseStorage = doc.url.includes('firebasestorage.googleapis.com');

        try {
          if (!isFirebaseStorage) {
            const response = await fetch(doc.url);
            const blob = await response.blob();

            if (doc.mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || doc.mimeType === 'text/plain') {
              extractedText = await extractFileContent(blob, doc.name);
              if (extractedText && typeof extractedText === 'string') {
                extractedText = extractedText.replace(/<[^>]*>?/gm, '\n');
              }
            } else {
              base64Data = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result.split(',')[1]);
                reader.readAsDataURL(blob);
              });
            }
          } else {
            // Use Proxy for Storage
            const proxyResult = await downloadFileViaProxy(doc.url);
            base64Data = proxyResult.base64Data;

            if (doc.mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || doc.mimeType === 'text/plain') {
              try {
                const binaryString = window.atob(base64Data);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                  bytes[i] = binaryString.charCodeAt(i);
                }
                const blob = new Blob([bytes], { type: doc.mimeType });
                extractedText = await extractFileContent(blob, doc.name);
                if (extractedText && typeof extractedText === 'string') {
                  extractedText = extractedText.replace(/<[^>]*>?/gm, '\n');
                }
              } catch (e) { console.error("Proxy text extraction error:", e); }
            }
          }
        } catch (err) {
          console.warn("Could not fetch dropped file content, sending URL only", err);
        }

        setPendingAttachments(prev => [
          ...prev,
          {
            mimeType: doc.mimeType,
            url: base64Data ? null : doc.url,
            data: base64Data,
            textContent: extractedText,
            name: doc.name,
            id: doc.id
          }
        ]);

        // Focus input
        setTimeout(() => {
          const input = document.getElementById(isMobile ? 'chatInput' : 'chatInputDesktop');
          if (input) input.focus();
        }, 100);
      }
    } catch (err) {
      console.error('Error handling drop in Chat:', err);
    }
  };

  return (
    <div
      id="view-chat"
      className="view active"
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
    >
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileUpload}
        accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
      />

      <div className="chat-pane">
        <div
          className="chat-history"
          id="chatHistory"
          ref={chatHistoryRef}
        >
          {messages.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">
                <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16 12C12.6863 12 10 14.6863 10 18V38C10 41.3137 12.6863 44 16 44H20L28 52V44H48C51.3137 44 54 41.3137 54 38V18C54 14.6863 51.3137 12 48 12H16Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M20 24H44M20 32H36" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </div>
              <h1 className="empty-title">Start with <span className="lawnance-brand">LAWNANCE</span></h1>
              <p className="empty-subtitle">Tax law, contract analysis, and more</p>

              {/* Try Asking Section */}
              <div className="w-full max-w-md mx-auto mt-8 px-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-white/50 mb-4">
                  TRY ASKING:
                </p>
                <div className="flex flex-col gap-3">
                  {suggestions.map((suggestion) => {
                    const IconComponent = suggestion.icon;
                    return (
                      <button
                        key={suggestion.id}
                        onClick={() => handleSuggestionClick(suggestion.text)}
                        className="flex items-center gap-3 p-3 rounded-xl border transition-all active:scale-98 suggestion-card-base"
                        style={{
                          minHeight: '56px',
                          backdropFilter: 'blur(12px)',
                          WebkitBackdropFilter: 'blur(12px)',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                        }}
                      >
                        <div
                          className="flex items-center justify-center rounded-lg flex-shrink-0 suggestion-icon-bg-base"
                          style={{
                            width: '40px',
                            height: '40px',
                          }}
                        >
                          <IconComponent size={20} />
                        </div>
                        <div className="flex-1 text-left">
                          <div className="text-sm font-medium text-white">
                            {suggestion.text}
                          </div>
                          <div className="text-xs uppercase tracking-wide mt-1" style={{ color: 'rgba(255, 255, 255, 0.4)' }}>
                            {suggestion.category}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <>
              {messages.map((message) => (
                <div key={message.id} className={`chat-message ${message.role}`}>
                  {message.role === 'assistant' ? (
                    <ReactMarkdown>{message.content}</ReactMarkdown>
                  ) : (
                    message.content
                  )}
                </div>
              ))}
              {/* Show streaming message */}
              {streamingMessage && (
                <div className="chat-message assistant">
                  <ReactMarkdown>{streamingMessage}</ReactMarkdown>
                  <span className="streaming-cursor">▊</span>
                </div>
              )}
              {/* Show loading indicator */}
              {isLoading && !streamingMessage && (
                <div className="chat-message assistant">
                  <span className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </span>
                </div>
              )}
              {/* Show error message */}
              {error && (
                <div className="chat-message system error">
                  ⚠️ {error}
                </div>
              )}
              {/* Show API not configured warning */}
              {!isGeminiAvailable && messages.length === 0 && (
                <div className="chat-message system warning">
                  ⚠️ Gemini API is not configured. Please add VITE_GEMINI_API_KEY to your .env file.
                </div>
              )}
            </>
          )}
        </div>

        {/* Desktop: Input bar inside chat-pane */}
        {!isMobile && (
          <div className="input-bar-wrapper">
            {/* Pending Attachments Preview (Desktop) */}
            {pendingAttachments.length > 0 && (
              <div className="flex flex-wrap gap-2 px-4 py-2 bg-white/5 border-t border-white/10">
                {pendingAttachments.map((att, idx) => (
                  <div key={`${att.id}-${idx}`} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 text-[11px] text-white/90 border border-white/10">
                    <FileText size={12} className="text-blue-400" />
                    <span className="max-w-[150px] truncate">{att.name}</span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setPendingAttachments(prev => prev.filter((_, i) => i !== idx));
                      }}
                      className="p-0.5 rounded-full hover:bg-white/20 transition-colors cursor-pointer"
                    >
                      <X size={10} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="input-bar">
              <div className="attach-btn-wrapper">
                <button
                  ref={attachButtonRefDesktop}
                  className="attach-btn"
                  id="chatAttachBtnDesktop"
                  title="Attach file"
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowAttachDropdown(prev => !prev);
                  }}
                >
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                  </svg>
                </button>
                <div ref={attachDropdownRefDesktop} className={`attach-dropdown ${showAttachDropdown ? 'show' : ''}`} id="chatAttachDropdownDesktop">
                  <button className="attach-menu-item" data-action="upload-photo" onClick={() => fileInputRef.current?.click()}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="2" y="4" width="12" height="10" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none" />
                      <circle cx="6" cy="7" r="1.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
                      <path d="M2 12L5 9L8 12L14 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Upload Photo
                  </button>
                  <button className="attach-menu-item" data-action="upload-file" onClick={() => fileInputRef.current?.click()}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4 2H10L14 6V14C14 14.5523 13.5523 15 13 15H4C3.44772 15 3 14.5523 3 14V3C3 2.44772 3.44772 2 4 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M10 2V6H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Upload File
                  </button>
                  <button className="attach-menu-item attach-menu-item-expandable" data-action="cloud-accounts" id="chatCloudBtnDesktop" onClick={() => setShowCloudSubmenu(!showCloudSubmenu)}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4 8C2.89543 8 2 8.89543 2 10C2 11.1046 2.89543 12 4 12H12C13.1046 12 14 11.1046 14 10C14 8.89543 13.1046 8 12 8C12 5.79086 10.2091 4 8 4C6.34315 4 4.84315 4.84315 4 6.34315" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Connect Cloud Accounts
                  </button>
                  <div className={`attach-cloud-submenu ${showCloudSubmenu ? 'show' : ''}`} id="chatCloudSubmenuDesktop">
                    <button onClick={handleGoogleDrivePick} className="attach-menu-item" data-action="google-drive">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 2L14 12H2L8 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M8 2L2 12H8V2Z" fill="currentColor" opacity="0.3" />
                      </svg>
                      Google Drive
                    </button>

                  </div>
                </div>
              </div>

              <div
                className={`input-container ${isCompact ? 'compact' : ''}`}
                onClick={handleContainerClick}
                style={{ cursor: 'text' }}
              >
                <input
                  ref={inputRef}
                  type="text"
                  className="input-field"
                  id="chatInputDesktop"
                  placeholder="Type your legal or finance question..."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                />
                <button
                  className="send-btn"
                  id="chatSendDesktop"
                  title="Send message"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSend();
                  }}
                  disabled={!inputValue.trim() && pendingAttachments.length === 0}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Mobile Chatbar */}
      {isMobile && (
        <div className="input-bar-wrapper">
          {/* Pending Attachments Preview (Mobile) */}
          {pendingAttachments.length > 0 && (
            <div className="flex flex-wrap gap-2 px-4 py-2 bg-white/5 border-t border-white/10">
              {pendingAttachments.map((att, idx) => (
                <div key={`${att.id}-${idx}`} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 text-[11px] text-white/90 border border-white/10">
                  <FileText size={12} className="text-blue-400" />
                  <span className="max-w-[150px] truncate">{att.name}</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setPendingAttachments(prev => prev.filter((_, i) => i !== idx));
                    }}
                    className="p-0.5 rounded-full hover:bg-white/20 transition-colors cursor-pointer"
                  >
                    <X size={10} />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="input-bar">
            <div className="attach-btn-wrapper">
              <button
                ref={attachButtonRefMobile}
                className="attach-btn"
                id="chatAttachBtn"
                title="Attach file"
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowAttachDropdown(prev => !prev);
                }}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 2V18M2 10H18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
              {isMobile && showAttachDropdown && createPortal(
                <div
                  ref={attachDropdownRefMobile}
                  className="attach-dropdown show"
                  id="chatAttachDropdown"
                  data-theme={getTheme()}
                  style={{
                    position: 'fixed',
                    zIndex: 99999,
                    opacity: 0,
                    visibility: 'hidden',
                    pointerEvents: 'none',
                    transition: 'opacity 0.15s ease-out',
                  }}
                >
                  <button className="attach-menu-item" data-action="upload-photo" onClick={() => fileInputRef.current?.click()}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="2" y="4" width="12" height="10" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none" />
                      <circle cx="6" cy="7" r="1.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
                      <path d="M2 12L5 9L8 12L14 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Upload Photo
                  </button>
                  <button className="attach-menu-item" data-action="upload-file" onClick={() => fileInputRef.current?.click()}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4 2H10L14 6V14C14 14.5523 13.5523 15 13 15H4C3.44772 15 3 14.5523 3 14V3C3 2.44772 3.44772 2 4 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M10 2V6H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Upload File
                  </button>
                  <button className="attach-menu-item attach-menu-item-expandable" data-action="cloud-accounts" id="chatCloudBtn" onClick={() => setShowCloudSubmenu(!showCloudSubmenu)}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4 8C2.89543 8 2 8.89543 2 10C2 11.1046 2.89543 12 4 12H12C13.1046 12 14 11.1046 14 10C14 8.89543 13.1046 8 12 8C12 5.79086 10.2091 4 8 4C6.34315 4 4.84315 4.84315 4 6.34315" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Connect Cloud Accounts
                  </button>
                  <div className={`attach-cloud-submenu ${showCloudSubmenu ? 'show' : ''}`} id="chatCloudSubmenu">
                    <button onClick={handleGoogleDrivePick} className="attach-menu-item" data-action="google-drive">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 2L14 12H2L8 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M8 2L2 12H8V2Z" fill="currentColor" opacity="0.3" />
                      </svg>
                      Google Drive
                    </button>

                  </div>
                </div>,
                document.body
              )}
            </div>

            <div
              className={`input-container ${isCompact ? 'compact' : ''}`}
              onClick={handleContainerClick}
              style={{ cursor: 'text' }}
            >
              <input
                ref={inputRef}
                type="text"
                className="input-field"
                id="chatInput"
                placeholder="Ask me anything..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <button
                className="send-btn"
                id="chatSend"
                title="Send message"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSend();
                }}
                disabled={!inputValue.trim() && pendingAttachments.length === 0}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2 7L12 2L7 12L6 7L2 7Z" fill="currentColor" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Blur Effect */}
      {isMobile && (
        <div
          className="fixed left-0 right-0 z-40 pointer-events-none"
          aria-hidden="true"
          style={{
            bottom: '0',
            height: 'calc(64px + env(safe-area-inset-bottom, 0px))',
            background: 'rgba(0, 0, 0, 0.4)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
          }}
        />
      )}

      {/* Mobile Documents Bottom Sheet */}
      {isMobile && (
        <MobileBottomSheet
          isOpen={showMobileDocuments}
          onClose={() => {
            setShowMobileDocuments(false);
            onCloseSidebar?.();
          }}
          title="Documents"
        >
          <div className="flex flex-col gap-4">
            <input
              type="search"
              className="w-full h-12 px-3 rounded-2xl border border-white/20 bg-white/12 backdrop-blur-xl outline-none text-base text-white"
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div className="flex gap-1.5 flex-wrap">
              {['All', 'Summary', 'Notes', 'Transcript'].map((filter) => (
                <button
                  key={filter}
                  onClick={() => setActiveFilter(filter.toLowerCase())}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium filter-chip-base ${activeFilter === filter.toLowerCase() ? 'active' : ''}`}
                >
                  {filter}
                </button>
              ))}
            </div>
            <div className="flex flex-col gap-1.5">
              {searchFilteredDocs.map((doc) => (
                <DocumentCard
                  key={doc.id}
                  document={doc}
                  onClick={handleDocumentClick}
                  theme={getTheme()}
                  showSkeleton={doc.loading}
                />
              ))}
            </div>
          </div>
        </MobileBottomSheet>
      )}
    </div>
  );
};

export default ChatView;
